import { useEffect, useRef, useState } from 'react';

import { IUnmatchedIdentifiers } from '@project44-models/shipment-list';

interface IUseTags {
  requestFn: (terms: string[]) => Promise<IUnmatchedIdentifiers>;
  onChange?: (tags: string[]) => void;
  initialValue?: string[];
}

export default function useTags({
  requestFn,
  onChange,
  initialValue = [],
}: IUseTags) {
  const hasUpdates = useRef<boolean>(!!initialValue.length);
  const [tags, setTags] = useState<string[]>(() =>
    initialValue.filter(Boolean),
  );
  const [status, setStatus] = useState<'idle' | 'loading' | 'success'>('idle');
  const [unmatchedTerms, setUnmatchedTerms] = useState<string[]>([]);

  useEffect(() => {
    const getUnmatchedTerms = (searchTerms: string[]) => {
      setStatus('loading');
      requestFn(searchTerms).then((data) => {
        const { terms = [] } = data;
        const sortedTags = Array.from(searchTerms);

        sortedTags.sort((a, b) => {
          if (terms.includes(a) && !terms.includes(b)) {
            return -1;
          }
          if (!terms.includes(a) && terms.includes(b)) {
            return 1;
          }
          return 0;
        });

        setTags(sortedTags);
        setUnmatchedTerms(terms);
        setStatus('success');
      });
    };

    if (hasUpdates.current) {
      getUnmatchedTerms(tags);
      hasUpdates.current = false;
    }
  }, [requestFn, tags]);

  const removeTag = (removedTag: string) => {
    setTags((prev) => {
      return prev.filter((tag) => tag !== removedTag);
    });

    if (onChange) {
      onChange(tags.filter((tag) => tag !== removedTag));
    }
  };

  const appendTags = (newTags: string[]) => {
    setStatus('idle');
    setTags((prev) => {
      hasUpdates.current = true;
      const nextTags = Array.from(new Set(prev.concat(newTags)));
      return nextTags;
    });

    // WARN: Causing a slow down. Fix coming...
    if (onChange) {
      onChange(tags.concat(newTags));
    }
  };

  const getTagStatus = (tag: string) => {
    if (status === 'success') {
      return unmatchedTerms.includes(tag) ? 'unmatched' : 'matched';
    }
    return status;
  };

  return {
    tags,
    setTags,
    appendTags,
    removeTag,
    getTagStatus,
  };
}
